import React from 'react'
import PropTypes from 'prop-types'

// Material-UI
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'

// App
import { useLoader } from 'genjo-ui/core/LoaderProvider'
import { useSnackbar } from 'genjo-ui/core/SnackbarProvider'
import { RecaptchaInput } from 'src/components/RecaptchaInput'
import { firebaseApp } from 'src/services/firebase'

import BackIcon from '@material-ui/icons/ArrowBackRounded'
import SuccessIcon from '@material-ui/icons/TaskAltRounded'


// We need to manually set the redirect for the Firebase reset function.
// See: https://stackoverflow.com/questions/55296314/firebase-redirect-to-webpage-after-successful-password-change
// Make sure that domain (login.basebuilders.com) is whitelisted by the FB
// auth sign-in-method settings.
const actionCodeSettings = {
  // After password reset, the user will be give the ability to go back
  // to this page.
  url: 'https://login.basebuilders.com/',
  handleCodeInApp: false
}

/**
 * Forgot password view
 * User can enter their email and the system will send a reset email. If the
 * user submits a valid email address that is not tied to an account no
 * indication will be given that there is not a registered user.
 *
 * Firebase docs:
 * https://firebase.google.com/docs/auth/web/manage-users
 */
export const ForgotPasswordForm = ({ onClose }) => {
  const loader = useLoader()
  const snackbar = useSnackbar()

  const [emailSent, setEmailSent] = React.useState(false)

  const [recaptchaIsConfirmed, setRecaptchaIsConfirmed] = React.useState(false)

  async function sendPasswordResetEmail(event) {
    event.preventDefault()

    if (!recaptchaIsConfirmed) {
      return
    }

    loader.open('Sending password reset email...')

    try {
      await firebaseApp.auth().sendPasswordResetEmail(
        event.target.recoveryEmail.value,
        actionCodeSettings,  // Manual redirect link back to site from FB.
      )

      setEmailSent(true)
    } catch (err) {
      console.log({ err })
      snackbar.addMessage({
        text: 'There was a problem sending password reset email.',
        type: 'error',
      })
    } finally {
      loader.close()
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent>
        {emailSent ? (
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ textAlign: 'center' }}>
              <SuccessIcon style={{ fontSize: 32 }} color="success" />
              <Typography variant="h6">
                Password Reset Email Sent
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography color="textSecondary" align="center">
                An email has been sent. Open the email and follow the instructions to reset your
                password.
              </Typography>
            </Grid>
            <Grid item>
              <Button
                onClick={onClose}
                startIcon={<BackIcon />}
              >
                Go Back
              </Button>
            </Grid>
          </Grid>
        ) : (
          <form
            onSubmit={sendPasswordResetEmail}
          >
            <Grid container spacing={2}>

              <Grid item xs={12}>
                <Typography variant="h6" align="center">
                  Recover Your Password
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography color="textSecondary" align="center">
                  {`Don't worry. It happens to the best of us.`}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Your email address"
                  variant="outlined"
                  type="email"
                  name="recoveryEmail"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <RecaptchaInput
                  confirm={() => setRecaptchaIsConfirmed(true)}
                  unconfirm={() => setRecaptchaIsConfirmed(false)}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container alignItems="center" justify="space-between">

                  <Grid item>
                    <Button
                      onClick={onClose}
                      startIcon={<BackIcon />}
                    >
                      Go Back
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      color="primary"
                      type="submit"
                      variant="contained"
                      disabled={!recaptchaIsConfirmed}
                    >
                      Send Password Recovery Email
                    </Button>
                  </Grid>
                </Grid>

              </Grid>

            </Grid>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

ForgotPasswordForm.propTypes = {
  /** Callback to return to the main sign in view. */
  onClose: PropTypes.func,
}
